import { useEffect, useState } from "react";

const sections = [
  { id: "introduction", label: "0.1 Introduction" },
  { id: "information-we-collect", label: "1. Information we collect" },
  { id: "how-we-use", label: "2. How we use your information" },
  { id: "ai-processing", label: "3. AI processing" },
  { id: "data-storage", label: "4. Data storage and security" },
  { id: "data-sharing", label: "5. Data sharing" },
  { id: "data-retention", label: "6. Data retention" },
  { id: "user-rights", label: "7. User rights" },
  { id: "cookies", label: "8. Cookies and tracking" },
  { id: "third-party", label: "9. Third-party links" },
  { id: "changes", label: "10. Changes to this privacy policy" },
  { id: "contact", label: "11. Contact us" },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("introduction");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(({ id }) => ({
        id,
        el: document.getElementById(id),
      }));

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const { id, el } = sectionElements[i];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 240) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 200;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="lg:w-[85%] w-[90%] mx-auto py-16 sm:py-24 mt-20">
      <h1 className="text-4xl sm:text-6xl font-semibold text-[#00003F] mb-12">
        Privacy Policy
      </h1>

      <div className="flex flex-col lg:flex-row gap-12 lg:gap-36">
        {/* Sidebar Navigation */}
        <nav className="shrink-0">
          <div className="lg:sticky lg:top-[120px]">
            <ul className="space-y-5 pl-4 lg:pt-[30px]">
              {sections.map(({ id, label }) => (
                <li key={id}>
                  <button
                    onClick={() => scrollToSection(id)}
                    className={`text-sm sm:text-lg lg:text-[22px] text-left transition-colors ${
                      activeSection === id
                        ? "text-[#000B55] font-medium"
                        : "text-[#667085] hover:text-[#000B55]"
                    }`}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 text-sm sm:text-lg lg:text-[22px]">
          {/* Introduction */}
          <section id="introduction" className="mb-12">
            <p className="text-sm sm:text-lg lg:text-[22px] text-[#667085] mb-2">
              Last Updated: 10th March, 2026.
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-1">
              PrecisionMatch ("we", "our", or "us") respects your privacy and is
              committed to protecting the personal information you share with us.
              This Privacy Policy explains how we collect, use, store, and
              protect your information when you use the PrecisionMatch platform,
              website, and related services.
            </p>
            <p className="text-[#1D2939] leading-relaxed">
              By using PrecisionMatch, you agree to the collection and use of
              information in accordance with this policy.
            </p>
          </section>

          {/* 1. Information we collect */}
          <section id="information-we-collect" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              1. Information we collect
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-6">
              We may collect several types of information in order to provide and
              improve our services.
            </p>

            <h3 className="font-semibold text-[#000B55] mb-3">
              1.1 Personal information
            </h3>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              When you create an account or use our services, we may collect:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-6">
              <li>Full name</li>
              <li>Email address</li>
              <li>Company name</li>
              <li>Job title or role</li>
              <li>Account login credentials</li>
            </ul>

            <h3 className="font-semibold text-[#000B55] mb-3">
              1.2 Hiring and candidate data
            </h3>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              When using PrecisionMatch for recruitment purposes, you may upload
              or input information related to job applicants, including:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>CVs and resumes</li>
              <li>Candidate names and contact information</li>
              <li>Professional experience and qualifications</li>
              <li>Skills and employment history</li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              This information is used solely to support the hiring evaluation
              features of the platform.
            </p>
          </section>

          {/* 2. How we use your information */}
          <section id="how-we-use" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              2. How we use your information
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>To provide and operate the PrecisionMatch platform</li>
              <li>
                To analyze and compare candidate CVs with job requirements
              </li>
              <li>To generate hiring insights and candidate evaluations</li>
              <li>To improve product functionality and user experience</li>
              <li>
                To communicate important updates, product releases, or service
                notifications
              </li>
              <li>
                To maintain the security and integrity of the platform
              </li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              We do not sell your personal information to third parties.
            </p>
          </section>

          {/* 3. AI processing */}
          <section id="ai-processing" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              3. AI processing
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              PrecisionMatch uses artificial intelligence to assist with tasks
              such as:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>CV analysis and comparison</li>
              <li>Job description generation</li>
              <li>Candidate evaluation insights</li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              These processes are designed to support hiring decisions but do not
              replace human judgment. Users remain responsible for their final
              hiring decisions.
            </p>
          </section>

          {/* 4. Data storage and security */}
          <section id="data-storage" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              4. Data storage and security
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              We take appropriate security measures to protect your information
              from unauthorized access, alteration, disclosure, or destruction.
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              These measures include:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5">
              <li>Secure servers and encrypted data transmission</li>
              <li>Access control mechanisms</li>
              <li>Regular system monitoring and security updates.</li>
            </ul>
          </section>

          {/* 5. Data sharing */}
          <section id="data-sharing" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              5. Data sharing
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-4">
              We may share information in the following circumstances:
            </p>

            <h3 className="text-base font-semibold text-[#000B55] mb-3">
              5.1 Service providers
            </h3>
            <p className="text-[#1D2939] leading-relaxed mb-6">
              We may work with trusted third-party providers who help us operate
              our platform, such as hosting services, analytics providers, or
              payment processors.
            </p>

            <h3 className="text-base font-semibold text-[#000B55] mb-3">
              5.2 Legal requirements
            </h3>
            <p className="text-[#1D2939] leading-relaxed mb-4">
              We may disclose information if required to do so by law or in
              response to valid legal requests.
            </p>
            <p className="text-[#1D2939] leading-relaxed">
              We do not share candidate or employer data with third parties for
              marketing purposes.
            </p>
          </section>

          {/* 6. Data retention */}
          <section id="data-retention" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              6. Data retention
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              We retain personal and candidate data only for as long as necessary
              to:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>Provide our services</li>
              <li>Maintain account functionality</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              Users may request deletion of their data at any time.
            </p>
          </section>

          {/* 7. User rights */}
          <section id="user-rights" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              7. User rights
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              You have rights regarding your personal data, including:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>Access to your personal information</li>
              <li>Correction of inaccurate data</li>
              <li>Deletion of your data</li>
              <li>Restriction of processing</li>
              <li>Data portability where applicable</li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              To exercise these rights, please contact us using the details
              below.
            </p>
          </section>

          {/* 8. Cookies and tracking */}
          <section id="cookies" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              8. Cookies and tracking
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-3">
              PrecisionMatch may use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-6 text-[#1D2939] space-y-1.5 mb-4">
              <li>Improve user experience</li>
              <li>Understand how the platform is used</li>
              <li>Maintain session functionality</li>
            </ul>
            <p className="text-[#1D2939] leading-relaxed">
              Users may control cookie preferences through their browser
              settings.
            </p>
          </section>

          {/* 9. Third-party links */}
          <section id="third-party" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              9. Third-party links
            </h2>
            <p className="text-[#1D2939] leading-relaxed">
              Our platform or website may contain links to third-party websites.
              We are not responsible for the privacy practices of those external
              sites.
            </p>
          </section>

          {/* 10. Changes to this privacy policy */}
          <section id="changes" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              10. Changes to this privacy policy
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-4">
              We may update this Privacy Policy from time to time to reflect
              product updates, legal requirements, or improvements to our
              services.
            </p>
            <p className="text-[#1D2939] leading-relaxed">
              When we make significant changes, we will notify users through the
              platform or via email.
            </p>
          </section>

          {/* 11. Contact us */}
          <section id="contact" className="mb-12">
            <h2 className="text-2xl font-bold text-[#000B55] mb-4">
              11. Contact us
            </h2>
            <p className="text-[#1D2939] leading-relaxed mb-4">
              If you have questions about this Privacy Policy or how your data is
              handled, please contact us:
            </p>
            <p className="text-[#1D2939] leading-relaxed mb-2">
              Email: contact@precisionmatch.ai
            </p>
            <p className="text-[#1D2939] leading-relaxed">
              Website: www.precisionmatch.ai
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export { PrivacyPolicy };
